import type { SessionService } from '@efdigital/ll-addon-auth';
import { debounce } from '@efdigital/ll-addon-ui';
import {
  buildSearchUsersByText,
  type UserInfo,
} from '@efdigital/ll-admin-v2/data/builders/system/users';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cell, resource, use } from 'ember-resources';

export type SearchUsersState = {
  text: string;
};

interface SearchUsersSignature {
  Element: HTMLDivElement;
  Args: {
    legend: string;
    state: Readonly<SearchUsersState>;
    setState: (state: Partial<SearchUsersState>) => void;
  };
  Blocks: {
    search: [Request];
  };
}

export class SearchUsers extends Component<SearchUsersSignature> {
  @service declare session: SessionService;

  get showResults(): boolean {
    return this.args.state.text.trim().length >= 3;
  }

  updateText = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.setState({ text: target.value });
  };

  get #searchUserRequest(): Promise<Request> | null {
    const trimmed = this.args.state.text.trim();
    if (trimmed.length < 3) {
      return null;
    }

    return buildSearchUsersByText(
      {
        text: trimmed,
      },
      this.session
    );
  }

  @use searchUserRequest = resource(({ on }) => {
    const state = cell<Request | null>(null);
    const requestPromise = this.#searchUserRequest;

    if (!requestPromise) {
      return () => state.current;
    }

    let cancelled = false;

    void requestPromise
      .then((request) => {
        if (!cancelled) {
          state.set(request);
        }
      })
      .catch(() => {
        if (!cancelled) {
          state.set(null);
        }
      });

    on.cleanup(() => {
      cancelled = true;
    });

    return () => state.current;
  });

  <template>
    <fieldset ...attributes>
      <legend class="gud-eyebrow">{{@legend}}</legend>
      <label class="visually-hidden" for="user-search-text">
        Search
      </label>
      <div class="input-group mb-2">
        <input
          id="user-search-text"
          type="search"
          class="form-control"
          placeholder="Search by email, name, or user ID"
          value={{@state.text}}
          autocomplete="off"
          data-test-user-search-input
          {{debounce this.updateText}}
        />
      </div>
    </fieldset>

    {{#if this.searchUserRequest}}
      {{yield this.searchUserRequest to="search"}}
    {{else}}
      <p class="text-body-secondary">
        Please enter a search term.
      </p>
    {{/if}}
  </template>
}

export async function processSearchUsersRequest(
  request: Request
): Promise<UserInfo[]> {
  const response = await fetch(request);
  if (!response.ok) {
    throw new Error(`Failed to fetch users: ${response.statusText}`);
  }
  const json = (await response.json()) as UserInfo[];
  return json;
}
